import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Star, Code2, ChevronRight, User, LogOut, Menu, Filter } from 'lucide-react';
import Button from '../../components/ui/Button';
import NavigationSidebar from './components/NavigationSidebar';
import ContentArea from './components/ContentArea';
import ProgressPanel from './components/ProgressPanel';
import TutorialCard from './components/TutorialCard';
import SearchFilters from './components/SearchFilters';
import Header from '../../components/ui/Header';

const TutorialLearningCenter = () => {
  const navigate = useNavigate();
  const { tutorialSlug, lessonId } = useParams();
  
  const [currentUser, setCurrentUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState('all');
  const [viewMode, setViewMode] = useState('browse'); // 'browse' or 'learning'
  const [currentTutorial, setCurrentTutorial] = useState(null);
  const [currentLesson, setCurrentLesson] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [bookmarks, setBookmarks] = useState([]);
  const [notes, setNotes] = useState({});
  const [userProgress, setUserProgress] = useState({});

  // Mock user authentication check
  useEffect(() => {
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser));
        // Load user progress, bookmarks, notes
        const savedProgress = localStorage.getItem(`progress_${JSON.parse(storedUser)?.id}`);
        const savedBookmarks = localStorage.getItem(`bookmarks_${JSON.parse(storedUser)?.id}`);
        const savedNotes = localStorage.getItem(`notes_${JSON.parse(storedUser)?.id}`);
        
        if (savedProgress) setUserProgress(JSON.parse(savedProgress));
        if (savedBookmarks) setBookmarks(JSON.parse(savedBookmarks));
        if (savedNotes) setNotes(JSON.parse(savedNotes));
      } catch (error) {
        console.error('Error parsing stored user:', error);
      }
    }
  }, []);

  // Comprehensive tutorials data for all 20 roadmaps
  const tutorials = [
    {
      id: 1,
      slug: 'manual-testing-fundamentals',
      title: 'Manual Testing Fundamentals',
      category: 'Manual Testing',
      difficulty: 'Beginner',
      description: 'Complete guide to manual testing methodologies, test case design, and quality assurance processes.',
      estimatedTime: '4 hours',
      lessons: 12,
      enrolled: 2500,
      rating: 4.8,
      author: 'Sarah Chen',
      lastUpdated: '2025-01-03',
      tags: ['Testing', 'QA', 'Test Cases', 'SDLC'],
      thumbnail: '/assets/tutorials/manual-testing.jpg',
      featured: true,
      prerequisites: ['Basic computer skills'],
      learningObjectives: [
        'Understand SDLC and testing phases',
        'Design effective test cases',
        'Execute manual testing strategies',
        'Report and track defects'
      ],
      chapters: [
        {
          id: 1,
          title: 'Introduction to Software Testing',
          lessons: [
            { id: '1-1', title: 'What is Software Testing?', duration: '15 min', type: 'video' },
            { id: '1-2', title: 'Types of Testing', duration: '20 min', type: 'interactive' },
            { id: '1-3', title: 'Testing in SDLC', duration: '18 min', type: 'video' }
          ]
        },
        {
          id: 2,
          title: 'Test Case Design',
          lessons: [
            { id: '2-1', title: 'Test Case Components', duration: '22 min', type: 'text' },
            { id: '2-2', title: 'Writing Effective Test Cases', duration: '25 min', type: 'interactive' },
            { id: '2-3', title: 'Test Case Templates', duration: '15 min', type: 'download' }
          ]
        },
        {
          id: 3,
          title: 'Test Execution & Reporting',
          lessons: [
            { id: '3-1', title: 'Test Execution Process', duration: '20 min', type: 'video' },
            { id: '3-2', title: 'Defect Reporting', duration: '18 min', type: 'interactive' },
            { id: '3-3', title: 'Test Reports & Metrics', duration: '16 min', type: 'text' }
          ]
        }
      ]
    },
    {
      id: 2,
      slug: 'python-automation-testing',
      title: 'Python Automation Testing',
      category: 'Automation Testing',
      difficulty: 'Intermediate',
      description: 'Master automation testing using Python, Selenium, and pytest framework with practical examples.',
      estimatedTime: '8 hours',
      lessons: 18,
      enrolled: 3200,
      rating: 4.9,
      author: 'Alex Rodriguez',
      lastUpdated: '2025-01-02',
      tags: ['Python', 'Selenium', 'Automation', 'Pytest'],
      thumbnail: '/assets/tutorials/python-automation.jpg',
      featured: true,
      prerequisites: ['Python basics', 'HTML/CSS knowledge'],
      learningObjectives: [
        'Set up Python testing environment',
        'Write Selenium automation scripts',
        'Implement pytest framework',
        'Handle dynamic web elements'
      ],
      chapters: [
        {
          id: 1,
          title: 'Python Testing Setup',
          lessons: [
            { id: '1-1', title: 'Environment Configuration', duration: '25 min', type: 'video' },
            { id: '1-2', title: 'Installing Dependencies', duration: '15 min', type: 'interactive' },
            { id: '1-3', title: 'First Python Test', duration: '30 min', type: 'code' }
          ]
        },
        {
          id: 2,
          title: 'Selenium WebDriver',
          lessons: [
            { id: '2-1', title: 'WebDriver Basics', duration: '35 min', type: 'video' },
            { id: '2-2', title: 'Locating Elements', duration: '40 min', type: 'interactive' },
            { id: '2-3', title: 'Element Interactions', duration: '30 min', type: 'code' }
          ]
        }
      ]
    },
    {
      id: 3,
      slug: 'api-testing-comprehensive',
      title: 'Comprehensive API Testing',
      category: 'API Testing',
      difficulty: 'Intermediate',
      description: 'Complete guide to REST API testing, GraphQL testing, and API automation strategies.',
      estimatedTime: '6 hours',
      lessons: 15,
      enrolled: 2800,
      rating: 4.7,
      author: 'Maria Garcia',
      lastUpdated: '2024-12-28',
      tags: ['API', 'REST', 'GraphQL', 'Postman'],
      thumbnail: '/assets/tutorials/api-testing.jpg',
      featured: true,
      prerequisites: ['HTTP protocol knowledge', 'JSON understanding'],
      learningObjectives: [
        'Understand API testing concepts',
        'Test REST and GraphQL APIs',
        'Automate API testing',
        'Validate API responses'
      ]
    },
    {
      id: 4,
      slug: 'javascript-test-automation',
      title: 'JavaScript Test Automation',
      category: 'Automation Testing',
      difficulty: 'Intermediate',
      description: 'Modern JavaScript testing with Cypress, Jest, and WebDriverIO for web applications.',
      estimatedTime: '7 hours',
      lessons: 16,
      enrolled: 1900,
      rating: 4.6,
      author: 'David Kim',
      lastUpdated: '2024-12-30',
      tags: ['JavaScript', 'Cypress', 'Jest', 'WebDriverIO'],
      prerequisites: ['JavaScript fundamentals', 'Web development basics']
    },
    {
      id: 5,
      slug: 'performance-testing-jmeter',
      title: 'Performance Testing with JMeter',
      category: 'Performance Testing',
      difficulty: 'Advanced',
      description: 'Master performance testing using Apache JMeter with load testing and performance optimization.',
      estimatedTime: '5 hours',
      lessons: 14,
      enrolled: 1200,
      rating: 4.5,
      author: 'Jennifer Liu',
      lastUpdated: '2024-12-25',
      tags: ['JMeter', 'Performance', 'Load Testing'],
      prerequisites: ['Testing experience', 'System architecture basics']
    },
    {
      id: 6,
      slug: 'mobile-testing-appium',
      title: 'Mobile Testing with Appium',
      category: 'Mobile Testing',
      difficulty: 'Advanced',
      description: 'Comprehensive mobile app testing for iOS and Android using Appium framework.',
      estimatedTime: '9 hours',
      lessons: 20,
      enrolled: 950,
      rating: 4.4,
      author: 'Michael Chang',
      lastUpdated: '2024-12-20',
      tags: ['Appium', 'Mobile', 'iOS', 'Android'],
      prerequisites: ['Mobile development basics', 'Automation experience']
    },
    {
      id: 7,
      slug: 'security-testing-fundamentals',
      title: 'Security Testing Fundamentals',
      category: 'Security Testing',
      difficulty: 'Advanced',
      description: 'Essential security testing concepts, OWASP guidelines, and vulnerability assessment.',
      estimatedTime: '6 hours',
      lessons: 13,
      enrolled: 780,
      rating: 4.3,
      author: 'Emily Johnson',
      lastUpdated: '2024-12-18',
      tags: ['Security', 'OWASP', 'Penetration Testing'],
      prerequisites: ['Testing experience', 'Basic security concepts']
    },
    {
      id: 8,
      slug: 'playwright-modern-testing',
      title: 'Modern Testing with Playwright',
      category: 'Automation Testing',
      difficulty: 'Intermediate',
      description: 'Next-generation browser automation with Playwright for cross-browser testing.',
      estimatedTime: '6 hours',
      lessons: 14,
      enrolled: 1400,
      rating: 4.8,
      author: 'Robert Wilson',
      lastUpdated: '2025-01-01',
      tags: ['Playwright', 'Cross-browser', 'Modern Testing'],
      featured: true,
      prerequisites: ['JavaScript/TypeScript', 'Web application basics']
    },
    {
      id: 9,
      slug: 'bdd-cucumber-testing',
      title: 'BDD with Cucumber',
      category: 'BDD Testing',
      difficulty: 'Intermediate',
      description: 'Behavior-Driven Development using Cucumber, Gherkin syntax, and collaborative testing.',
      estimatedTime: '5 hours',
      lessons: 11,
      enrolled: 1600,
      rating: 4.5,
      author: 'Lisa Anderson',
      lastUpdated: '2024-12-22',
      tags: ['BDD', 'Cucumber', 'Gherkin', 'Collaboration'],
      prerequisites: ['Basic programming', 'Agile methodology']
    },
    {
      id: 10,
      slug: 'java-automation-testing',
      title: 'Java Automation Testing',
      category: 'Automation Testing',
      difficulty: 'Intermediate',
      description: 'Build robust automation frameworks using Java, Selenium WebDriver, and TestNG.',
      estimatedTime: '10 hours',
      lessons: 22,
      enrolled: 2100,
      rating: 4.7,
      author: 'Kevin Martinez',
      lastUpdated: '2024-12-27',
      tags: ['Java', 'Selenium', 'TestNG', 'Framework'],
      prerequisites: ['Java basics', 'OOP concepts', 'Manual testing experience']
    },
    {
      id: 11,
      slug: 'ci-cd-testing-pipeline',
      title: 'CI/CD Testing Pipeline',
      category: 'DevOps Testing',
      difficulty: 'Advanced',
      description: 'Integration of automated testing in CI/CD pipelines using Jenkins and containerization.',
      estimatedTime: '7 hours',
      lessons: 16,
      enrolled: 890,
      rating: 4.6,
      author: 'Thomas Brown',
      lastUpdated: '2024-12-24',
      tags: ['CI/CD', 'Jenkins', 'Docker', 'Pipeline'],
      prerequisites: ['DevOps basics', 'Automation experience']
    },
    {
      id: 12,
      slug: 'accessibility-testing-guide',
      title: 'Accessibility Testing Guide',
      category: 'Accessibility Testing',
      difficulty: 'Intermediate',
      description: 'Comprehensive accessibility testing using WCAG guidelines and automated tools.',
      estimatedTime: '4 hours',
      lessons: 10,
      enrolled: 720,
      rating: 4.4,
      author: 'Rachel Green',
      lastUpdated: '2024-12-19',
      tags: ['Accessibility', 'WCAG', 'Inclusive Design'],
      prerequisites: ['Web testing experience', 'Accessibility awareness']
    },
    {
      id: 13,
      slug: 'test-automation-architecture',
      title: 'Test Automation Architecture',
      category: 'Test Architecture',
      difficulty: 'Expert',
      description: 'Design scalable, maintainable test automation architectures for enterprise applications.',
      estimatedTime: '8 hours',
      lessons: 17,
      enrolled: 540,
      rating: 4.7,
      author: 'Daniel Lee',
      lastUpdated: '2024-12-21',
      tags: ['Architecture', 'Framework Design', 'Scalability'],
      prerequisites: ['Senior automation experience', 'Architecture knowledge']
    },
    {
      id: 14,
      slug: 'data-driven-testing',
      title: 'Data-Driven Testing',
      category: 'Test Design',
      difficulty: 'Intermediate',
      description: 'Master data-driven testing approaches and test data management strategies.',
      estimatedTime: '5 hours',
      lessons: 12,
      enrolled: 980,
      rating: 4.5,
      author: 'Amy Chen',
      lastUpdated: '2024-12-23',
      tags: ['Data-Driven', 'Test Data', 'Parameterization'],
      prerequisites: ['Automation experience', 'Database basics']
    },
    {
      id: 15,
      slug: 'exploratory-testing-techniques',
      title: 'Exploratory Testing Techniques',
      category: 'Exploratory Testing',
      difficulty: 'Intermediate',
      description: 'Advanced exploratory testing techniques and session-based testing approaches.',
      estimatedTime: '4 hours',
      lessons: 9,
      enrolled: 1100,
      rating: 4.6,
      author: 'Steve Wilson',
      lastUpdated: '2024-12-26',
      tags: ['Exploratory', 'Session-based', 'Creative Testing'],
      prerequisites: ['Manual testing experience', 'Critical thinking']
    }
  ];

  // Filter tutorials based on search and filters
  const filteredTutorials = tutorials?.filter((tutorial) => {
    const matchesSearch = tutorial?.title?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         tutorial?.description?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
                         tutorial?.tags?.some(tag => tag?.toLowerCase()?.includes(searchTerm?.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || tutorial?.category === selectedCategory;
    const matchesDifficulty = selectedDifficulty === 'all' || tutorial?.difficulty === selectedDifficulty;
    
    return matchesSearch && matchesCategory && matchesDifficulty;
  });

  // Get featured tutorials
  const featuredTutorials = tutorials?.filter(tutorial => tutorial?.featured);

  // Load tutorial by slug
  useEffect(() => {
    if (tutorialSlug) {
      const tutorial = tutorials?.find(t => t?.slug === tutorialSlug);
      if (tutorial) {
        setCurrentTutorial(tutorial);
        setViewMode('learning');
        
        if (lessonId) {
          // Find the specific lesson
          const lesson = tutorial?.chapters
            ?.flatMap(chapter => chapter?.lessons)
            ?.find(lesson => lesson?.id === lessonId);
          if (lesson) {
            setCurrentLesson(lesson);
          }
        } else if (tutorial?.chapters?.length > 0) {
          // Start with first lesson
          setCurrentLesson(tutorial?.chapters?.[0]?.lessons?.[0]);
        }
      }
    } else {
      setViewMode('browse');
      setCurrentTutorial(null);
      setCurrentLesson(null);
    }
  }, [tutorialSlug, lessonId]);

  const handleTutorialStart = (tutorial) => {
    navigate(`/tutorial-learning-center/${tutorial?.slug}`);
  };

  const handleLessonSelect = (lesson) => {
    setCurrentLesson(lesson);
    navigate(`/tutorial-learning-center/${currentTutorial?.slug}/${lesson?.id}`);
  };

  const handleBookmark = (tutorialId) => {
    if (!currentUser) return;
    
    const newBookmarks = bookmarks?.includes(tutorialId)
      ? bookmarks?.filter(id => id !== tutorialId)
      : [...bookmarks, tutorialId];
    
    setBookmarks(newBookmarks);
    localStorage.setItem(`bookmarks_${currentUser?.id}`, JSON.stringify(newBookmarks));
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/90 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <Code2 className="w-6 h-6 text-white" />
              </div>
              <Link to="/" className="text-xl font-bold hover:text-blue-500 transition-colors">
                Testing University
              </Link>
              
              {/* Breadcrumb */}
              {currentTutorial && (
                <div className="hidden md:flex items-center space-x-2 text-sm">
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  <Link to="/tutorial-learning-center" className="text-blue-600 hover:underline">
                    Tutorials
                  </Link>
                  <ChevronRight className="w-4 h-4 text-muted-foreground" />
                  <span className="text-muted-foreground">{currentTutorial?.title}</span>
                </div>
              )}
            </div>
            
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">Home</Link>
              <Link to="/testing-roadmaps-hub" className="text-muted-foreground hover:text-foreground transition-colors">Roadmaps</Link>
              <Link to="/tutorial-learning-center" className="text-foreground font-medium">Tutorials</Link>
              <Link to="/challenges" className="text-muted-foreground hover:text-foreground transition-colors">Challenges</Link>
            </div>

            <div className="flex items-center space-x-3">
              {viewMode === 'learning' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSidebarOpen(!sidebarOpen)}
                  className="md:hidden"
                >
                  <Menu className="w-4 h-4" />
                </Button>
              )}
              
              {currentUser ? (
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                      <User className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-medium">{currentUser?.username || currentUser?.email}</span>
                  </div>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/user-profile-dashboard')}
                  >
                    Dashboard
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => {
                      localStorage.removeItem('currentUser');
                      setCurrentUser(null);
                    }}
                  >
                    <LogOut className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => navigate('/user-login')}
                  >
                    Login
                  </Button>
                  <Button 
                    size="sm"
                    onClick={() => navigate('/user-registration')}
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>
      {/* Updated Navigation with consistent header structure */}
      <Header user={currentUser} onLogout={() => {
        localStorage.removeItem('currentUser');
        setCurrentUser(null);
      }} />
      {/* Add proper top padding for fixed header */}
      {viewMode === 'browse' ? (
        /* Browse Mode - Tutorial Directory */
        (<div className="pt-16">
          {/* Hero Section */}
          <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-purple-50 dark:from-gray-900 dark:to-gray-800">
            <div className="max-w-7xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h1 className="text-4xl lg:text-5xl font-bold mb-6">
                  Tutorial Learning Center
                  <span className="block bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                    Master Testing Through Practice
                  </span>
                </h1>
                <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
                  Comprehensive tutorials covering all aspects of testing. From fundamental concepts 
                  to advanced techniques with interactive lessons and hands-on exercises.
                </p>
                
                {/* Quick Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-12">
                  <div className="text-center">
                    <div className="text-3xl font-bold text-blue-600">{tutorials?.length}</div>
                    <div className="text-sm text-muted-foreground">Tutorials</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-purple-600">200+</div>
                    <div className="text-sm text-muted-foreground">Lessons</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-green-600">15,000+</div>
                    <div className="text-sm text-muted-foreground">Students</div>
                  </div>
                  <div className="text-center">
                    <div className="text-3xl font-bold text-orange-600">4.6★</div>
                    <div className="text-sm text-muted-foreground">Avg Rating</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
          {/* Search and Filters */}
          <section className="py-8 px-4 sm:px-6 lg:px-8 border-b border-border">
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                <div className="flex-1 max-w-2xl">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                    <input
                      type="text"
                      placeholder="Search tutorials, technologies, or topics..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-3 border border-border rounded-lg bg-background focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div className="flex items-center space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center space-x-2"
                  >
                    <Filter className="w-4 h-4" />
                    <span>Filters</span>
                  </Button>
                </div>
              </div>
              
              {showFilters && (
                <SearchFilters
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                  selectedDifficulty={selectedDifficulty}
                  setSelectedDifficulty={setSelectedDifficulty}
                  tutorials={tutorials}
                />
              )}
            </div>
          </section>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            {/* Featured Tutorials */}
            {featuredTutorials?.length > 0 && !searchTerm && (
              <motion.div
                className="mb-12"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-2xl font-bold mb-6 flex items-center space-x-2">
                  <Star className="w-6 h-6 text-yellow-500" />
                  <span>Featured Tutorials</span>
                </h2>
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {featuredTutorials?.map((tutorial) => (
                    <TutorialCard
                      key={tutorial?.id}
                      tutorial={tutorial}
                      onStart={() => handleTutorialStart(tutorial)}
                      onBookmark={() => handleBookmark(tutorial?.id)}
                      isBookmarked={bookmarks?.includes(tutorial?.id)}
                      userProgress={userProgress?.[tutorial?.id]}
                      featured={true}
                    />
                  ))}
                </div>
              </motion.div>
            )}

            {/* All Tutorials */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">
                  {searchTerm ? `Search Results (${filteredTutorials?.length})` : 'All Tutorials'}
                </h2>
                
                <div className="text-sm text-muted-foreground">
                  {filteredTutorials?.length} of {tutorials?.length} tutorials
                </div>
              </div>

              {filteredTutorials?.length > 0 ? (
                <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                  {filteredTutorials?.map((tutorial) => (
                    <TutorialCard
                      key={tutorial?.id}
                      tutorial={tutorial}
                      onStart={() => handleTutorialStart(tutorial)}
                      onBookmark={() => handleBookmark(tutorial?.id)}
                      isBookmarked={bookmarks?.includes(tutorial?.id)}
                      userProgress={userProgress?.[tutorial?.id]}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No tutorials found</h3>
                  <p className="text-muted-foreground">
                    Try adjusting your search terms or filters to find what you're looking for.
                  </p>
                </div>
              )}
            </motion.div>
          </div>
        </div>)
      ) : (
        /* Learning Mode - Three Panel Layout */
        (<div className="pt-16 flex h-screen">
          {/* Navigation Sidebar */}
          <div className={`${sidebarOpen ? 'w-80' : 'w-0'} transition-all duration-300 overflow-hidden border-r border-border`}>
            <NavigationSidebar
              tutorial={currentTutorial}
              currentLesson={currentLesson}
              onLessonSelect={handleLessonSelect}
              userProgress={userProgress?.[currentTutorial?.id]}
            />
          </div>
          {/* Main Content Area */}
          <div className="flex-1 flex overflow-hidden">
            <div className="flex-1 overflow-auto">
              <ContentArea
                tutorial={currentTutorial}
                lesson={currentLesson}
                notes={notes}
                setNotes={setNotes}
                currentUser={currentUser}
              />
            </div>

            {/* Progress Panel */}
            <div className="w-80 border-l border-border overflow-auto">
              <ProgressPanel
                tutorial={currentTutorial}
                lesson={currentLesson}
                userProgress={userProgress?.[currentTutorial?.id]}
                setUserProgress={setUserProgress}
                currentUser={currentUser}
                userStats={{}}
              />
            </div>
          </div>
        </div>)
      )}
    </div>
  );
};

export default TutorialLearningCenter;